import React, { useEffect, useState } from 'react';

// All field options are now managed in the backend manifest

// Form validation utilities
const validateField = (name, value, type) => {
  const errors = [];
  
  switch (name.toLowerCase()) {
    case 'email':
      if (!value || value.trim() === '') {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        errors.push('Please enter a valid email address');
      }
      break;
      
    case 'description':
      if (!value || value.trim() === '') {
        errors.push('Description is required');
      } else if (value.trim().length < 10) {
        errors.push('Description must be at least 10 characters long');
      } else if (value.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }
      break;
      
    case 'address':
      if (!value || value.trim() === '') {
        errors.push('Address is required');
      } else if (value.trim().length < 5) {
        errors.push('Address must be at least 5 characters long');
      } else if (value.trim().length > 200) {
        errors.push('Address must be less than 200 characters');
      }
      break;
  }
  
  return errors;
};

// DynamicField: renders a form input based on control definition
// control: { name, label, type, options, required }
const DynamicField = ({ control = {}, value, onChange, showValidation = false }) => {
  // Provide default values for control properties
  const {
    name = 'field',
    label = 'Field',
    type = 'text',
    options = [],
    required = false
  } = control || {};

  const [imagePreview, setImagePreview] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  // Validate field when value changes and component should show validation
  useEffect(() => {
    // Show validation if explicitly requested OR if field has been touched
    const shouldValidate = showValidation && (hasBeenTouched || isValidatedField(name));
    
    if (shouldValidate) {
      const errors = validateField(name, value, type);
      setValidationErrors(errors);
    }
  }, [value, showValidation, hasBeenTouched, name, type]);

  // Helper to determine if this is a field we always want to validate on submit
  // Note: This function defines which fields should be validated when they exist in the form
  // The actual filtering of available fields happens in EditIssue.js
  const isValidatedField = (fieldName) => {
    const validatedFields = ['email', 'description', 'address'];
    return validatedFields.includes(fieldName.toLowerCase());
  };

  // Handle field blur to mark as touched
  const handleBlur = () => {
    setHasBeenTouched(true);
    if (showValidation) {
      const errors = validateField(name, value, type);
      setValidationErrors(errors);
    }
  };

  // If control is undefined, render a warning message
  if (!control) {
    console.warn(`DynamicField received undefined control`);
    return (
      <div className="alert alert-warning">
        Field configuration not found
      </div>
    );
  }

  // Update image preview when value changes
  useEffect(() => {
    if (type === 'file' && value) {
      // If value is a base64 string, use it directly
      if (typeof value === 'string' && value.startsWith('data:image')) {
        setImagePreview(value);
      } else if (typeof value === 'string') {
        // If it's just the base64 part, add the data URL prefix
        setImagePreview(`data:image/jpeg;base64,${value}`);
      }
    }
  }, [value, type]);

  // Use options from backend manifest (passed via control)
  const fieldOptions = options;

  // Determine if field has validation errors
  const hasErrors = validationErrors.length > 0;
  const shouldShowErrors = showValidation && (hasBeenTouched || isValidatedField(name)) && hasErrors;

  // Helper to render validation errors
  const renderValidationErrors = () => {
    if (!shouldShowErrors) return null;
    
    return (
      <div className="invalid-feedback d-block">
        {validationErrors.map((error, index) => (
          <div key={index} className="text-danger small">
            {error}
          </div>
        ))}
      </div>
    );
  };

  // Helper to render field label with red asterisk for required fields
  const renderFieldLabel = () => {
    const isRequired = isValidatedField(name);
    return (
      <label htmlFor={name} className="form-label">
        {label}
        {isRequired && <span style={{ color: 'red', marginLeft: '2px' }}>*</span>}
      </label>
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a preview URL for the selected image
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImagePreview(null);
    }
    // Call the original onChange handler
    onChange(e);
  };

  switch (type) {
    case 'select':
      return (
        <div className="mb-3" key={name}>
          {renderFieldLabel()}
          <select
            id={name}
            name={name}
            className={`form-select ${shouldShowErrors ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={onChange}
            onBlur={handleBlur}
            required={required}
          >
            <option value="">Select {label}</option>
            {fieldOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {renderValidationErrors()}
        </div>
      );

    case 'textarea':
      return (
        <div className="mb-3" key={name}>
          {renderFieldLabel()}
          <textarea
            id={name}
            name={name}
            className={`form-control ${shouldShowErrors ? 'is-invalid' : ''}`}
            rows="3"
            value={value || ''}
            onChange={onChange}
            onBlur={handleBlur}
            required={required}
          />
          {renderValidationErrors()}
        </div>
      );

    case 'checkbox':
      return (
        <div className="form-check mb-3" key={name}>
          <input
            id={name}
            name={name}
            type="checkbox"
            className="form-check-input"
            checked={!!value}
            onChange={onChange}
          />
          <label htmlFor={name} className="form-check-label">
            {label}
            {isValidatedField(name) && <span style={{ color: 'red', marginLeft: '2px' }}>*</span>}
          </label>
        </div>
      );

    case 'file':
      return (
        <div className="mb-3" key={name}>
          {renderFieldLabel()}
          <input
            id={name}
            name={name}
            type="file"
            className="form-control mb-2"
            onChange={handleImageChange}
            accept="image/*"
          />
          {imagePreview && (
            <div className="mt-2">
              <img
                src={imagePreview}
                alt="Preview"
                className="img-thumbnail"
                style={{ maxHeight: '200px', maxWidth: '100%' }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-danger mt-2"
                onClick={() => {
                  setImagePreview(null);
                  // Simulate a file input change with no file selected
                  onChange({ target: { name, type: 'file', files: [] } });
                }}
              >
                Remove Image
              </button>
            </div>
          )}
        </div>
      );

    case 'date':
    case 'time':
    case 'number':
    case 'email':
    case 'tel':
    case 'text':
    default:
      return (
        <div className="mb-3" key={name}>
          {renderFieldLabel()}
          <input
            id={name}
            name={name}
            type={type}
            className={`form-control ${shouldShowErrors ? 'is-invalid' : ''}`}
            value={value || ''}
            onChange={onChange}
            onBlur={handleBlur}
            required={required}
          />
          {renderValidationErrors()}
        </div>
      );
  }
};

export default DynamicField;